import { CollabItem } from 'components/CollabItem'
import { List } from 'components/CollabItem/List/List'
import { useRootStore } from 'stores/RootStore'
import { CommentV2Props } from './Types'
import { observer } from 'mobx-react-lite'
import { useContext, useEffect, useRef } from 'react'
import { computed, reaction } from 'mobx'
import { Thread } from './Thread/Thread'
import { NewReplyForm } from './NewReplyForm/NewReplyForm'
import { CommentItem } from './CommentItem/CommentItem'
import { ReplyItem } from './ReplyItem/ReplyItem'
import { AlmanacEditorStateContext } from 'stores/EditorState'
import { schema } from 'prose-core'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { matchPath, useLocation } from 'react-router-dom'
import { ARTICLE_REVIEW_PATH, FOLDER_ARTICLE_REVIEW_PATH } from 'paths'

const CommentV2 = observer(({ items, inCleanupMode = false }: CommentV2Props) => {
  const rootStore = useRootStore()
  const location = useLocation()
  const comment = items[0]
  const isActive = computed(() => rootStore.comments.selected?.id === comment.id).get()
  const isHighlighted = computed(() => rootStore.comments.highlighted?.id === comment.id).get()
  const { isMobile } = useResponsiveDesign()

  const ref = useRef<HTMLDivElement>(null)
  const createReplyFormInputRef = useRef<HTMLTextAreaElement>(null)
  const collapsed = rootStore.comments?.selected?.id !== comment.id

  /* -------------------------------------------------------------------------------------------------
   * Mouse interactions
   * -----------------------------------------------------------------------------------------------*/

  const handleClick = () => {
    if (isActive) {
      return
    }
    rootStore.comments.select(comment.id)
    rootStore.docStore?.collabItemsStore.select(comment.uuid)
    rootStore.comments.markAsRead(comment.id)
    setTimeout(() => {
      createReplyFormInputRef.current?.focus()
    }, 50)
  }

  const handleMouseOver = () => {
    if (isActive || isHighlighted) {
      return
    }
    rootStore.comments.highlight(comment.id)
    rootStore.docStore?.collabItemsStore.highlight(comment.uuid)
  }

  const handleMouseLeave = () => {
    rootStore.comments.highlight(undefined)
    rootStore.docStore?.collabItemsStore.highlight(undefined)
  }

  // NOTE: I replaced CollabItem.Root.onClickOutside with this reaction
  // Setting `rootStore.comments.selected` to false when clicking another commented node causes a flicker
  // Checking if the new selection is a commented node itself prevents the flicker
  const almanacState = useContext(AlmanacEditorStateContext)
  useEffect(() =>
    reaction(
      () => isActive && !almanacState?.activeMarks.has(schema.marks.comment),
      (lostSelection) =>
        lostSelection && comment.id === rootStore.comments.selected?.id && rootStore.comments.select(undefined),
    ),
  )

  const handleClickOutside = () => {
    const inReviewsModal = matchPath(location.pathname, {
      path: ARTICLE_REVIEW_PATH,
      exact: true,
      strict: false,
    })

    const inReviewsModalFolder = matchPath(location.pathname, {
      path: FOLDER_ARTICLE_REVIEW_PATH,
      exact: true,
      strict: false,
    })

    if (isActive && !isMobile && !inReviewsModal && !inReviewsModalFolder) {
      rootStore.comments.select(undefined)
      rootStore.docStore?.collabItemsStore.select(undefined)
    }
  }

  return (
    <CollabItem.Root
      isActive={isActive}
      isHighlighted={isHighlighted}
      onClick={handleClick}
      onClickOutside={handleClickOutside}
      onMouseOver={handleMouseOver}
      onMouseLeave={handleMouseLeave}
      ref={ref}
    >
      <Thread items={items}>
        <List collapsed={collapsed} onToggle={handleClickOutside} itemId={comment.id}>
          <CommentItem comment={comment} inCleanupMode={inCleanupMode} hasReplies={items.length > 1} />
          {items.slice(1).map((item) => (
            <ReplyItem key={item.id} comment={item} inCleanupMode={inCleanupMode} />
          ))}
        </List>
        <CollabItem.ReplyForm>
          <NewReplyForm item={comment} inputRef={createReplyFormInputRef} />
        </CollabItem.ReplyForm>
      </Thread>
    </CollabItem.Root>
  )
})

export { CommentV2 }
